import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Spinner, PageLoader } from "@/components/common";
import AuthShell from "./AuthShell";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
      toast.success("Check your email");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Forgot password" subtitle="We'll email you a secure reset link." footer={<Link to="/login/student" className="font-semibold text-[#1F6FFF]">Back to login</Link>}>
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div className="space-y-2"><Label>Email</Label><Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" data-testid="forgot-email-input" /></div>
        {msg && <p className="rounded-lg bg-[#3CC34A]/10 px-3 py-2 text-sm text-[#1f8f2b]" data-testid="forgot-success">{msg}</p>}
        <Button disabled={busy} className="h-11 w-full rounded-full bg-[#1F6FFF] hover:bg-[#1450C9]" data-testid="forgot-submit-button">{busy && <Spinner />} Send reset link</Button>
      </form>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token: sp.get("token"), password: pw });
      toast.success(data.message);
      nav("/login/student");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Set a new password">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div className="space-y-2"><Label>New password</Label><Input required minLength={8} type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 bg-white" data-testid="reset-password-input" /></div>
        <Button disabled={busy} className="h-11 w-full rounded-full bg-[#1F6FFF] hover:bg-[#1450C9]" data-testid="reset-submit-button">{busy && <Spinner />} Update password</Button>
      </form>
    </AuthShell>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const [state, setState] = useState(null);
  useEffect(() => {
    api.get("/auth/verify-email", { params: { token: sp.get("token") } })
      .then((r) => setState({ ok: true, msg: r.data.message }))
      .catch((e) => setState({ ok: false, msg: errMsg(e) }));
  }, [sp]);
  return (
    <AuthShell title="Email verification">
      {!state ? <PageLoader /> : (
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="verify-result">
          {state.ok ? <CheckCircle2 className="h-10 w-10 text-[#3CC34A]" /> : <XCircle className="h-10 w-10 text-red-500" />}
          <p className="text-slate-600" data-testid="verify-message">{state.msg}</p>
          <div className="flex gap-4 text-sm font-semibold text-[#1F6FFF]">
            <Link to="/login/student">Student login</Link><Link to="/login/tutor">Tutor login</Link>
          </div>
        </div>
      )}
    </AuthShell>
  );
}

export function ChangePassword() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      setUser({ ...user, must_change_password: false });
      toast.success("Password updated");
      nav(homeFor(user));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Change your password" subtitle="For security, set a new password before continuing.">
      <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
        {[["current_password", "Current password"], ["new_password", "New password (min 8)"], ["confirm", "Confirm new password"]].map(([k, l]) => (
          <div key={k} className="space-y-2"><Label>{l}</Label>
            <Input required minLength={k === "current_password" ? 1 : 8} type="password" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-11 bg-white" data-testid={`change-${k.replace("_", "-")}-input`} /></div>
        ))}
        <Button disabled={busy} className="h-11 w-full rounded-full bg-[#1F6FFF] hover:bg-[#1450C9]" data-testid="change-password-submit">{busy && <Spinner />} Update password</Button>
      </form>
    </AuthShell>
  );
}
